import { useState } from "react";
import { useAuth } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { GraduationCap, Video, MessageSquare, Award, ChevronRight, ChevronLeft } from "lucide-react";

const TOUR_KEY = "fscd_tour_v1";

const STEPS = {
  trainee: [
    { icon: GraduationCap, title: { en: "Learn at your pace", hi: "अपनी गति से सीखें" }, text: { en: "Your courses live on the Home tab — bilingual lessons, quizzes and exams. Practical skills are submitted as videos your instructor reviews.", hi: "आपके कोर्स होम टैब पर हैं — द्विभाषी पाठ, क्विज़ और परीक्षाएं। प्रैक्टिकल कौशल वीडियो के रूप में जमा होते हैं जिनकी समीक्षा आपके प्रशिक्षक करते हैं।" } },
    { icon: Video, title: { en: "Join live classes", hi: "लाइव क्लास में शामिल हों" }, text: { en: "The Live tab lists scheduled classes and your 1-on-1 sessions. Tap Join and you're in a built-in video room — no Zoom account needed.", hi: "लाइव टैब में निर्धारित क्लास और आपके 1-on-1 सत्र हैं। Join दबाएं और आप ऐप के अंदर वीडियो रूम में हैं — किसी Zoom खाते की ज़रूरत नहीं।" } },
    { icon: MessageSquare, title: { en: "Everything in Messages", hi: "सब कुछ संदेशों में" }, text: { en: "Chat with instructors, share photos of your work, join your class's group chat and see announcements — all inside the app.", hi: "प्रशिक्षकों से चैट करें, अपने काम की तस्वीरें साझा करें, क्लास ग्रुप चैट में जुड़ें और घोषणाएं देखें — सब ऐप के अंदर।" } },
    { icon: Award, title: { en: "Earn verified certificates", hi: "सत्यापित प्रमाणपत्र पाएं" }, text: { en: "Complete a track to earn an FSCD certificate with a QR code employers can verify — and download it as a PDF to share.", hi: "ट्रैक पूरा कर FSCD प्रमाणपत्र पाएं — QR कोड से नियोक्ता सत्यापित कर सकते हैं, और PDF डाउनलोड कर साझा करें।" } },
  ],
  staff: [
    { icon: GraduationCap, title: { en: "Your teaching hub", hi: "आपका शिक्षण केंद्र" }, text: { en: "Review practical submissions, track every student's progress in the Students roster, and build courses with the Course Builder.", hi: "प्रैक्टिकल सबमिशन की समीक्षा करें, Students सूची में हर छात्र की प्रगति देखें, और Course Builder से कोर्स बनाएं।" } },
    { icon: Video, title: { en: "Run live classes", hi: "लाइव क्लास चलाएं" }, text: { en: "Schedule classes (even weekly series), start 1-on-1 video sessions from any chat, and check attendance with absentee follow-ups.", hi: "क्लास शेड्यूल करें (साप्ताहिक सीरीज़ भी), किसी भी चैट से 1-on-1 वीडियो सत्र शुरू करें, और उपस्थिति व अनुपस्थित छात्रों का फॉलो-अप देखें।" } },
    { icon: MessageSquare, title: { en: "Communicate in one place", hi: "एक ही जगह संवाद" }, text: { en: "Direct chats, course announcements, class group rooms and file sharing — no email threads to chase.", hi: "सीधी चैट, कोर्स घोषणाएं, क्लास ग्रुप रूम और फ़ाइल साझा करना — ईमेल की ज़रूरत नहीं।" } },
    { icon: Award, title: { en: "Reports at a glance", hi: "रिपोर्ट एक नज़र में" }, text: { en: "Dashboards show live enrollment, certification and attendance data — export any of it to CSV for leadership.", hi: "डैशबोर्ड लाइव नामांकन, प्रमाणन और उपस्थिति डेटा दिखाते हैं — नेतृत्व के लिए CSV में निर्यात करें।" } },
  ],
};

const UI = {
  welcome: { en: "Welcome to FSCD", hi: "FSCD में स्वागत है" },
  step: { en: "Step", hi: "चरण" },
  of: { en: "of", hi: "में से" },
  next: { en: "Next", hi: "अगला" },
  back: { en: "Back", hi: "पीछे" },
  start: { en: "Get started", hi: "शुरू करें" },
  skip: { en: "Skip tour", hi: "टूर छोड़ें" },
};

export default function WelcomeTour() {
  const { user } = useAuth();
  const { pick } = useLang();
  const [done, setDone] = useState(() => localStorage.getItem(TOUR_KEY) === "1");
  const [step, setStep] = useState(0);
  if (!user || done) return null;
  const steps = ["assessor", "admin", "superadmin"].includes(user.role) ? STEPS.staff : STEPS.trainee;
  const finish = () => { localStorage.setItem(TOUR_KEY, "1"); setDone(true); };
  const s = steps[step];
  const Icon = s.icon;
  const last = step === steps.length - 1;

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center px-4" data-testid="welcome-tour">
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl p-7 text-center space-y-4">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-teal-800 text-white flex items-center justify-center">
          <Icon size={30} strokeWidth={1.8} />
        </div>
        <p className="text-[10px] font-bold uppercase tracking-widest text-teal-700">{pick(UI.welcome)} · {pick(UI.step)} {step + 1} {pick(UI.of)} {steps.length}</p>
        <h2 data-testid={`tour-step-${step}`} className="font-heading text-xl font-extrabold text-slate-900">{pick(s.title)}</h2>
        <p className="text-sm text-slate-500 leading-relaxed">{pick(s.text)}</p>
        <div className="flex items-center justify-center gap-1.5">
          {steps.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-teal-700" : "w-1.5 bg-slate-200"}`} />)}
        </div>
        <div className="flex gap-2 pt-1">
          {step > 0 && (
            <button data-testid="tour-back-btn" onClick={() => setStep(step - 1)}
              className="h-11 px-4 rounded-xl border border-slate-200 text-slate-500 text-sm font-semibold inline-flex items-center gap-1 hover:bg-slate-50">
              <ChevronLeft size={15} /> {pick(UI.back)}
            </button>
          )}
          <button data-testid="tour-next-btn" onClick={() => (last ? finish() : setStep(step + 1))}
            className="flex-1 h-11 rounded-xl bg-teal-800 hover:bg-teal-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-1 transition-colors">
            {last ? pick(UI.start) : pick(UI.next)} {!last && <ChevronRight size={15} />}
          </button>
        </div>
        <button data-testid="tour-skip-btn" onClick={finish} className="text-xs text-slate-400 hover:text-slate-600">{pick(UI.skip)}</button>
      </div>
    </div>
  );
}
